import React, { useEffect, useState } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SalesSummary from "./SalesSummary";
import loader from "../../assets/loader.webp";
import { adminAxios } from "../../axiosInstance";
import { formatNumbers } from "../../components/formatNumbers";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";
import TopSales from "./TopSales";
import LowStockHome from "./LowStockHome";
import AIinsight from "./AIinsight";
import ExpensesOverview from "./ExpensesOverview";
import LowstockChart from "./LowstockChart";
import StoreHealthPanel from "./StoreHealthPanel";

const ReportsPage = () => {
  const [overviewData, setOverviewData] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [, setPerformance] = useState({
    revenue: 0,
    prevRevenue: 0,
    count: 0,
    percentChange: 0,
    loaded: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState("₦");
  const [selectedPeriod] = useState("today");

  // Fetch dashboard and summary data based on selectedPeriod
  useEffect(() => {
    setIsLoading(true);

    async function fetchData() {
      try {
        const response = await adminAxios.get("dashboard");
        setOverviewData(response?.data?.data || {});

        // Adapted from requested code
        const d = response?.data?.data || {};
        let revenue = 0, prevRevenue = 0, count = 0;
        switch (selectedPeriod) {
          case "today":
            revenue = Number(d.todaysTotalRevenue || 0);
            prevRevenue = Number(d.yesterdaysTotalRevenue || 0);
            count = Number(d.todaysSalesCount || 0);
            break;
          case "week":
            revenue = Number(d.thisWeeksTotalRevenue || 0);
            prevRevenue = Number(d.lastWeeksTotalRevenue || 0);
            count = Number(d.thisWeeksSalesCount || 0);
            break;
          case "month":
            revenue = Number(d.thisMonthsTotalRevenue || 0);
            prevRevenue = Number(d.lastMonthsTotalRevenue || 0);
            count = Number(d.thisMonthsSalesCount || 0);
            break;
          case "year":
            revenue = Number(d.thisYearsTotalRevenue || 0);
            prevRevenue = Number(d.lastYearsTotalRevenue || 0);
            count = Number(d.thisYearsSalesCount || 0);
            break;
          default:
            break;
        }
        // Calculate percentChange
        let percentChange = 0;
        if (prevRevenue === 0 && revenue === 0) percentChange = 0;
        else if (prevRevenue === 0) percentChange = 100;
        else percentChange = ((revenue - prevRevenue) / Math.abs(prevRevenue)) * 100;

        setPerformance({
          revenue,
          prevRevenue,
          count,
          percentChange,
          loaded: true,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Error fetching data", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [selectedPeriod]);

  useEffect(() => {
    setCurrency(overviewData?.currency?.symbol || "₦");
    handleCheckUserRole();
  }, [overviewData]);
  // Check user role for admin/manager
  const handleCheckUserRole = () => {
    const userInfo = sessionStorage.getItem("token");
    const newUser = JSON.parse(userInfo);
    setIsAdmin(newUser && (newUser.role === "Admin" || newUser.role === "Manager"));
  };

  if (isLoading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <img src={loader} alt="loader" className="w-10 lg:w-20" />
      </div>
    );
  }

  return (
    <div className="flex flex-col-reverse lg:flex-row gap-6 px-4 py-4 bg-[#F0F1F3] min-h-screen">
        
        <div className="flex flex-col gap-6 w-full lg:w-[75%]">
        {/* Sales Overview */}
        <div className="flex flex-col bg-white rounded-lg gap-6 p-4">
            <h3 className="text-2xl font-medium">Sales Overview</h3>
            <div className="flex flex-col lg:flex-row gap[20%] gap-4 justify-between">
            <div className="flex flex-col gap-1 items-center">
                <BarChartIcon className="text-[#629FF4]" sx={{ fontSize: "40px" }} />
                <div className="flex font-medium text-lg flex-row gap-3">
                <h3>{formatNumbers(overviewData?.lowStockCount)}</h3>
                <h3>Restock Needed</h3>
                </div>
            </div>
            <div className="flex flex-col gap-1 items-center">
                <LocalAtmIcon className="text-[#629FF4]" sx={{ fontSize: "40px" }} />
                <div className="flex gap-3 font-medium text-lg flex-row ">
                <h3>
                    {currency}
                    {formatNumbersWithDecimal(overviewData?.todaysTotalRevenue)}
                </h3>
                <h3>Total Revenue</h3>
                </div>
            </div>
            <div className="flex flex-col gap-1 items-center">
                <TrendingUpIcon className="text-[#629FF4]" sx={{ fontSize: "40px" }} />
                <div className="flex gap-3 font-medium text-lg flex-row ">
                <h3>{overviewData?.todaysSalesCount}</h3>
                <h3>Sales Today</h3>
                </div>
            </div>
            </div>
        </div>
      
        {/* Sales Summary Chart */}
        <SalesSummary period={selectedPeriod} />
        
        <div className="flex flex-col-reverse lg:flex-row lg:items-stretch mt-5 gap-5 bg-[#F0F1F3]">
            <div className="w-full min-w-0 lg:w-1/2">
              {/* Low Stock Chart */}
              <LowstockChart />
            </div>
            <div className="w-full min-w-0 lg:w-1/2">
              {/* Expenses Overview Donut Chart */}
              <ExpensesOverview currency={currency} />
            </div>
        </div>
      
        {/* Top Sales Table */}
        <div className="flex flex-col bg-white rounded-lg gap-6 p-4 ">
            <div className="flex justify-between">
              <h3 className="text-2xl font-medium ">Top Sales</h3>
            </div>
            <div className="overflow-x-scroll">
              <table className="min-w-full bg-white border border-gray-300 ">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="py-2 px-4 border-b border-gray-300">Rank</th>
                    <th className="py-2 px-4 border-b border-gray-300">Product Name</th>
                    <th className="py-2 px-4 border-b border-gray-300 text-wrap">Qty Sold</th>
                    <th className="py-2 px-4 border-b border-gray-300 text-wrap">Total Sales</th>
                  </tr>
                </thead>
                <TopSales currency={currency} />
              </table>
            </div>
          </div>
        </div>
        <div className="flex flex-grow flex-col rounded-lg w-auto gap-6 lg:w-[25%]">
          <StoreHealthPanel />

          <div className="bg-white rounded-lg p-2">
            <h3 className="text-2xl font-medium ">Low Stock</h3>
            <LowStockHome />
          </div>
          {isAdmin && <AIinsight />}
        </div>
    </div>
  );
};

export default ReportsPage;